import { MapUtils } from '../../utility/map-utils.js';
import { Language } from '../serializable/language.js';
import { cloneMap, DataModel, DataModelBuilder, isSameMap,
  mapToData,
  SerializationContext, SerializedData } from '../dto/dto.js';
import { VersionRange } from '../core/version-range.js';
import { isNotNullOrUndefinedOrEmpty, isNullOrUndefinedOrEmpty } from '../../utility/string.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { isNotNullOrUndefined } from '../../utility/null-safety.js';
import { getAnyFromLocalizationMap, getAnyFromLocalizationMapOrDefault } from '../../utility/lang-utils.js';

export class LocalizedString extends DataModel {
  static readonly TYPE_ID: string = 'c18987a4c9d24afb97faaf2d4f2c83e2';
  static readonly SCHEMA_VERSION: number = 3;

  constructor(
    public localizations: Map<Language, string>,
  ) {
    super();
  }

  get $typeId(): string {
    return LocalizedString.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(LocalizedString.SCHEMA_VERSION);
  }

  isEmpty(): boolean {
    if(this.localizations.size == 0) {
      return true;
    }

    let isEmpty: boolean = true;
    for(const value of this.localizations.values()) {
      if(value.length > 0) {
        isEmpty = false;
        break;
      }
    }

    return isEmpty;
  }

  isNotEmpty(): boolean {
    return !this.isEmpty();
  }

  has(language: Language): boolean {
    return isNotNullOrUndefined(this.localizations.get(language));
  }

  hasAndNotEmpty(language: Language): boolean {
    return isNotNullOrUndefinedOrEmpty(this.localizations.get(language));
  }

  set(language: Language, value: string) {
    this.localizations.set(language, value);
  }

  get(language: Language): string | null {
    return this.localizations.get(language) ?? null;
  }

  getAny(language: Language | null = null): string | null {
    return getAnyFromLocalizationMap(this.localizations, language, (v) => v.length > 0);
  }

  getAnyOrEmpty(language: Language | null = null): string {
    return getAnyFromLocalizationMap(this.localizations, language, (v) => v.length > 0) ?? '';
  }

  getAnyOrDefault({ defValue, language }: {defValue: string, language?: Language | null}): string {
    return getAnyFromLocalizationMapOrDefault(this.localizations, defValue, language, (v) => v.length > 0);
  }

  static empty(): LocalizedString {
    return new LocalizedString(new Map<Language, string>());
  }

  static localized(localizations: Map<Language, string>): LocalizedString {
    return new LocalizedString(localizations);
  }

  static forLanguage(language: Language, description: string): LocalizedString {
    return new LocalizedString(new Map<Language, string>([[language, description]]));
  }

  static fromMap(context: SerializationContext, map: SerializedData): LocalizedString {
    return new LocalizedString(
        MapUtils.getMap<Language, string>(map, 'localizations', (k, v) => {
          return { key: toEnumFromString(k, Language), value: v as string };
        }),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'localizations': mapToData(this.localizations, (k, v) => {
        return { key: fromEnumAsString(k), value: v };
      }),
    };
  }

  clone(): this {
    return new LocalizedString(
        cloneMap(this.localizations),
    ) as this;
  }

  isSame(other: LocalizedString): boolean {
    if(other === this) {
      return true;
    }

    return isSameMap(other.localizations, this.localizations);
  }
}

export class LocalizedStringBuilder extends DataModelBuilder<LocalizedString> {
  build(context: SerializationContext, map: SerializedData): LocalizedString {
    return LocalizedString.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(LocalizedString.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(LocalizedString.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version we removed the [description] field.
    if(version < 2) {
      version = 2;

      const language = context.storage.get<Language>('language', Language.English);
      if(isNotNullOrUndefined(language)) {
        const text = MapUtils.get<string>(map, 'text');
        const localizedText = MapUtils.getMap<Language, string>(map, 'localizedText', (k, v) => {
          return { key: toEnumFromString(k, Language), value: v as string };
        });

        if(isNullOrUndefinedOrEmpty(localizedText.get(language))) {
          localizedText.set(language, text);
        }

        map['localizedText'] = mapToData(localizedText, (k, v) => {
          return { key: fromEnumAsString(k), value: v };
        });
      }
    }

    // In this version we renamed [localizedText] to [localizations].
    if(version < 3) {
      version = 3;
      map['localizations'] = map['localizedText'];
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}

